import axios, { type AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import env from '@/config/env';
import { HARD_LOGOUT_CODES, extractErrorCode } from '@/lib/api/auth-codes';

// Separate axios instance — notification service is proxied at /notifications/api/v1, not /core/api/v1
const notificationClient = axios.create({
  baseURL: `${env.API_BASE_URL.replace('/core/api/v1', '')}/notifications/api/v1`,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

notificationClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Notifications are non-critical, so this client does not run its own token
// refresh (the main apiClient owns that, and the next poll picks up the
// refreshed token). It only honours hard-logout codes so a revoked/deactivated
// session is torn down here too instead of silently retrying with a dead token.
notificationClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const code = extractErrorCode(error);
      if (code && HARD_LOGOUT_CODES.has(code)) {
        useAuthStore.getState().logout({ reason: code.toLowerCase() });
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
          window.location.assign('/auth/login');
        }
      }
    }
    return Promise.reject(error);
  },
);

export interface NotificationItem {
  id: string;
  subject: string;
  notificationStatus: 'DELIVERED' | 'READ';
  type: string;
  createdAt: string;
  // Sent by the backend real-time payload (platform-notifier); optional so the
  // REST list shape (which omits them) stays compatible.
  content?: string;
  metadata?: Record<string, unknown>;
}

export interface PaginatedNotifications {
  data: NotificationItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface NotificationCounts {
  total: number;
  unread: number;
  read: number;
}

export const notificationsService = {
  async list(limit = 20): Promise<PaginatedNotifications> {
    const { data } = await notificationClient.get<PaginatedNotifications>(
      '/notifications/user/all',
      { params: { page: 1, limit } },
    );
    return data;
  },

  /** Paginated fetch for the panel; optional status filter (DELIVERED = unread). */
  async listPage(
    page: number,
    limit = 15,
    status?: 'DELIVERED' | 'READ',
  ): Promise<PaginatedNotifications> {
    const { data } = await notificationClient.get<PaginatedNotifications>(
      '/notifications/user/all',
      { params: { page, limit, ...(status ? { status } : {}) } },
    );
    return data;
  },

  async counts(): Promise<NotificationCounts> {
    const { data } = await notificationClient.get<NotificationCounts>(
      '/notifications/user/counts',
    );
    return data;
  },

  async markAsRead(id: string): Promise<NotificationItem> {
    const { data } = await notificationClient.patch<NotificationItem>(
      `/notifications/${id}/read`,
    );
    return data;
  },

  async markAllRead(): Promise<void> {
    await notificationClient.patch('/notifications/user/read-all');
  },

  async remove(id: string): Promise<void> {
    await notificationClient.delete(`/notifications/${id}`);
  },
};
